export interface ReferrerName {
  id: number;
  code: string;
  name: string;
  cuit?: string | null;
  commissionPercentage?: number | null;
  enabled?: boolean;
}

export interface EconomicGroup {
  id: number;
  code: string;
  name: string;
}

export interface loginData {
  user: string;
  password: string;
}

export interface basicData {
  company_name: string;
  company_address: string;
  state: string;
  city: string;
  phone_number: string;
  email: string;
  legal_entity: string;
  activity: string;
  iva_status: string;
  date_incorporation: string;
  economic_group: string;
  legal_company_address: string;
  legal_city: string;
  legal_state: string;
  /** Gerencia (id de application.management); también en formulario paso 1 */
  management_id?: number | string;
  postal_code?: string | null;
  legal_postal_code?: string | null;
  contractual_link?: boolean | null;
  politically_exposed?: boolean | null;
  state_supplier_registry?: boolean | null;
  dgi_status?: string | null;
  dgi_issue_date?: string | null;
  dgi_expiration_date?: string | null;
  bps_certificate_number?: string | null;
  bps_issue_date?: string | null;
  bps_expiration_date?: string | null;
}

export interface corporateStructureData {
  id: number;
  person_id: number;
  ci: string;
  first_name: string;
  last_name: string;
  ownership_interest: string;
  position: string;
  assets: string | undefined;
  assets_file_path?: string | undefined;
  value: number;
}

export interface documentData {
  file: string;
  file_name: string;
  type: string;
  is_basic_data: boolean;
}

export interface cloudDocument {
  type: string;
  fullUrl: string;
  label: string;
  documentId: string;
  sourcePeriodYear?: number;
  source_period_year?: number;
}
export interface clientData {
  id: string;
  cuit: string;
  last_comment: string;
  status?: {
    id: string;
    code: string;
    label: string;
  };
  basic_data: basicData;
  corporate_structure: Array<corporateStructureData>;
  files: Array<documentData>;
  products: Array<applicationData>;
  /** Líneas de solicitud comercial (ORIGINATION); si no existe, usar `products`. */
  requested_product_lines?: Array<applicationData>;
  verified: boolean;
  manual_total_amount: boolean;
  requested_amount: Array<amount>;
  extra_comments: string;
  analyst_notes?: string;
  reason: string;
  review: string;
  has_valid_corporate_composition: boolean;
  signers: signersData[];
  referrer_name: string | ReferrerName;
  approved_amount?: Array<amount>;
  approved_products?: Array<applicationData>;
  id_application?: number;
  application_files: Array<applicationFile>;
  note_amount: null | number;
  guarantee_amount: null | number;
  instrumentation_id?: string;
  officer: string;
  category_type?: {
    id: number;
    name: string;
  };
  category_type_id?: number;
  effective_date?: string;
  credit_type_id?: number;
  credit_type_name?: string;
  balance_processing_status?: string | null;
  balance_processing_error_code?: string | null;
  balance_processing_error_context?: string | null;
  management_id?: number;
  management_label?: string;
  /** Distribución por SGR devuelta por el BFF cuando `is_multi_sgr_approval_enabled` está activo. */
  sgr_distributions?: SgrDistributionResponseItem[];
  operation_type_id?: number;
  operation_type_label?: string;
  current_sgr_balance?: number | null;
  lufe_balance_source_year?: number | null;
  assigned_risk_analyst_id?: number;
  assigned_risk_analyst_label?: string;
  /** Operaciones Públicas sindicadas (folder-level). */
  is_syndicated?: boolean;
  /** Create-time allowlist stamp; null/false = not eligible for remarcado. */
  syndication_eligible_at_create?: boolean | null;
  /** Validity block of the detail view (Task 53633): label/legend arrive resolved from the backend (RB10). */
  validity?: ValidityData | null;
}

/** Catálogo SGR (request/response genérico). */
export interface SgrCatalogItem {
  id: number;
  name: string;
  code: string;
  is_movil: boolean;
  enabled: boolean;
}

/** Payload PATCH `sgr_distributions`. */
export interface SgrDistributionItem {
  sgr_code: string;
  product_line_code: string;
  currency_code: string;
  value: number;
  block_total_ars?: number;
  block_total_usd?: number;
  is_manual_block_total?: boolean;
}

/** Filas de GET application detail / BFF (camelCase). */
export interface SgrDistributionResponseItem {
  sgrName: string;
  sgrCode: string;
  isMovil: boolean;
  productLineName: string;
  productLineCode: string;
  currencyCode: string;
  amount: number;
  blockTotalArs?: number;
  blockTotalUsd?: number;
  isManualBlockTotal?: boolean;
}
export interface applicationData {
  id: number;
  product_line_code: string;
  amount: amount;
  has_assets?: boolean;
  asset_code?: string;
  asset_count?: number;
  asset_name?: string;
}
export interface applicationInfo {
  products: Array<applicationData>;
  manual_total_amount: boolean;
  requested_amount: Array<amount>;
  reason: string;
  review: string;
  extra_comments: string;
  has_valid_corporate_composition: boolean;
  signers: Array<signersData>;
  category_type_id?: number;
  operation_type_id?: number;
  current_sgr_balance?: number | null;
  lufe_balance_source_year?: number | null;
  assigned_risk_analyst_id?: number;
  /** Operaciones Públicas sindicadas. */
  is_syndicated?: boolean;
}
export interface amount {
  currency_code: string;
  value: number | string;
}

export interface commentData {
  status_code: string;
  comment: string;
  analyst_notes?: string;
  approved_products?: {
    products: applicationData[];
    /**
     * Soft-rename (Option A): wire field keeps `*_ars` but means **tenant local currency bucket**
     * (ARS or UYU per `localCurrencyCode`), not Argentina-only. Do not rename the JSON key.
     */
    manual_approved_total_amount_ars: number;
    manual_approved_total_amount_usd: number;
  };
  sgr_distributions?: SgrDistributionItem[];
  operation_type_id?: number;
  current_sgr_balance?: number | null;
  /** Vigencia (Task 53632): ISO yyyy-MM-dd, only on FIN close. */
  start_date?: string;
  end_date?: string;
}

export interface postBalanceSheetData {
  id: number;
  monthId: string | number | null;
  month?: number | null;
  year?: number | null;
  amount: amount;
}

/** NOSIS rejected-check detail — camelCase inner object from ms-application via BFF. */
export type NosisDetailStatus = 'REJECTED' | 'NO_REJECTIONS' | 'NO_DATA';

export type NosisCountAmount = {
  count: number | null;
  amount: number | null;
};

export type NosisRejectionHistory = {
  lastRejectionDate: string | null;
  insufficientFunds: {
    unpaid: NosisCountAmount;
    paid: NosisCountAmount;
    finePending: NosisCountAmount;
  };
  otherReasons: {
    unpaid: NosisCountAmount;
  };
};

export type NosisPjBandMetrics = {
  totalCount: number;
  totalAmount: number;
  unpaidCount: number;
  unpaidAmount: number;
  paidCount: number;
  paidAmount: number;
};

export type NosisPjBandTotals = {
  rejected: NosisCountAmount;
  paid: NosisCountAmount;
  paidPct: number | null;
};

export type NosisPjSection = {
  columns: string[];
  rows: Array<{
    reason: string;
    bands: Record<string, NosisPjBandMetrics>;
  }>;
  totals: Record<string, NosisPjBandTotals>;
};

export type NosisCheckRow = {
  checkNumber: string;
  rejectionDate: string;
  amount: number;
  reason: string;
  paymentDate: string | null;
  daysToPayment: number | null;
  finePaid: string | null;
  finePaymentDate: string | null;
  relatedCuit: string | null;
};

export type NosisMonthlyEvolution = {
  yearMonth: string;
  rejected: NosisCountAmount;
  paid: NosisCountAmount;
  pendingCumulative: NosisCountAmount;
  available: boolean;
};

export type NosisFineBucket = {
  count: number;
  from: string | null;
  to: string | null;
};

export type NosisFinePayments = {
  paid: NosisFineBucket;
  unpaid: NosisFineBucket;
};

export type NosisRedNosis = {
  reported: { m3: number | null; m6: number | null; m12: number | null };
  positive: {
    m6Count: number | null;
    m6Amount: number | null;
    m7to12Count: number | null;
    m7to12Amount: number | null;
    m13to24Count: number | null;
    m13to24Amount: number | null;
  };
  pendingDeposit6m: NosisCountAmount;
  roundAmounts: {
    m3: number | null;
    m6: number | null;
    m12: number | null;
    m24: number | null;
  };
};

export type NosisRejectedChecksDetail = {
  detailStatus: NosisDetailStatus;
  pjStatus: NosisDetailStatus;
  rejectionHistory: NosisRejectionHistory | null;
  pj: NosisPjSection | null;
  checks: NosisCheckRow[] | null;
  monthlyEvolution: NosisMonthlyEvolution[] | null;
  finePayments: NosisFinePayments | null;
  redNosis: NosisRedNosis | null;
};

export interface riskData {
  id: number;
  risk_code: string;
  execution_date: string;
  last_execution?: string;
  indicators: Array<indicator>;
  previous_bpb_indicators?: indicator[];
  /** BFF snake_case: data switch for NOSIS_011–029 set. */
  nosis_check_detail_enabled?: boolean;
  /** BFF snake_case; inner keys remain camelCase from ms-application. */
  nosis_check_detail?: NosisRejectedChecksDetail | null;
  error?: {
    code: number;
    message: string;
  };
  totals?: {
    total_current: string;
    total_non_current: string;
    total_net_worth: string;
    total_net_worth_and_non_current: string;
    gross_result: string;
    operating_result: string;
    ordinary_result: string;
    result_before_taxes: string;
    taxes: string;
  };
  debts?: any;
}

export interface indicator {
  id: number;
  code: string;
  description: string;
  value: string;
  status: string;
  /** Agrupador desde catálogo de riesgo (ms-application / risk_analysis.risk_indicator). */
  category?: string | null;
  tooltip_message: string;
  /** Texto explicativo opcional (≠ description); expand UI solo si trim no vacío. */
  help_text?: string | null;
  document?: string;
  assets?: string;
  amount?: string | number;
  total_value?: number;  // Valor total desde el backend (snake_case)
  ownership_interest?: number;  // Porcentaje de participación desde el backend (snake_case)
}

// Interfaces para la estructura estandarizada de balance
export interface BalanceTotals {
  total_current: string;
  total_non_current: string;
  total_net_worth: string;
  total_net_worth_and_non_current: string;
  gross_result: string;
  operating_result: string;
  ordinary_result: string;
  result_before_taxes: {
    extraordinary_income: string;
    extraordinary_expenses: string;
    total: number;
    totalFormatted?: string;
  };
  result_before_taxes_calculated: number;
  taxes: string;
  income_tax_formatted?: string;
}

export interface BalanceValues {
  id: number;
  cuit: string;
  code: string;
  from_month: number;
  from_year: number;
  to_month: number;
  to_year: number;
  current_assets: {
    current_investments: number;
    cash_and_cash_equivalents: number;
    trade_receivables_short_term: number;
    other_receivables: number;
    inventory_short_term: number;
    other_current_assets: number;
  };
  non_current_assets: {
    trade_receivables_long_term: number;
    other_receivables: number;
    permanent_investments: number;
    property_plant_and_equipment: number;
    other_non_current_assets: number;
  };
  current_liabilities: {
    trade_payables_short_term: number;
    bank_and_financial_debt_short_term: number;
    social_security_debt_short_term: number;
    tax_liabilities_short_term: number;
    other_debts_provisions: number;
    projections: number;
    other_liabilities: number;
  };
  non_current_liabilities: {
    non_current_liabilities: number;
    trade_payables_long_term: number;
    bank_and_financial_debt_long_term: number;
    social_security_debt_long_term: number;
    tax_liabilities_long_term: number;
    other_long_term_liabilities: number;
  };
  non_controlling_interest_result: {
    non_controlling_interest_result: number;
  };
  net_worth: {
    capital: number;
    legal_reserves?: number;
    other_reserves?: number;
    capital_adjustment?: number;
    irrevocable_contributions?: number;
    technical_revaluation?: number;
    prior_accumulated?: number;
    result_of_period?: number;
    other?: number;
    other_components?: number; // Mantener para compatibilidad con datos antiguos
  };
  gross_result: {
    agricultural_production: number;
    production_cost: number;
    sales: number;
    cost_of_sales: number;
    /** Resultado bruto total; enviado a origination para EBIT. */
    total?: number;
  };
  operating_result: {
    commercialization_expenses: number;
    administrative_expenses: number;
    other_income: number;
    other_expenses: number;
  };
  ordinary_result: {
    financial_expenses: number;
    exchange_difference_and_holding_result: number;
  };
  result_before_taxes: {
    extraordinary_income: number;
    extraordinary_expenses: number;
  };
  taxes: {
    income_tax: number;
  };
  purchases?: number;
  amortization_depreciation?: number;
}

export interface StandardizedBalanceData {
  values: BalanceValues;
  totals: BalanceTotals;
}

export interface balanceInfo {
  id: number;
  /** Código del balance (balance_sheet_code) cuando viene de la API, usado en vista dual. */
  code?: string;
  from_month: number;
  from_year: number;
  to_month: number;
  to_year: number;
  current_assets: {
    current_investments: string;
    cash_and_cash_equivalents: string;
    trade_receivables_short_term: string;
    other_receivables: string;
    inventory_short_term: string;
    other_current_assets: string;
  };
  non_current_assets: {
    trade_receivables_long_term: string;
    other_receivables: string;
    permanent_investments: string;
    property_plant_and_equipment: string;
    other_non_current_assets: string;
  };
  current_liabilities: {
    trade_payables_short_term: string;
    bank_and_financial_debt_short_term: string;
    social_security_debt_short_term: string;
    tax_liabilities_short_term: string;
    other_debts_provisions: string;
    projections: string;
    other_liabilities: string;
  };
  non_current_liabilities: {
    non_current_liabilities: string;
    trade_payables_long_term: string;
    bank_and_financial_debt_long_term: string;
    social_security_debt_long_term: string;
    tax_liabilities_long_term: string;
    other_long_term_liabilities: string;
  };
  non_controlling_interest_result: {
    non_controlling_interest_result: string;
  };
  net_worth: {
    capital: string;
    legal_reserves?: string;
    other_reserves?: string;
    capital_adjustment?: string;
    irrevocable_contributions?: string;
    technical_revaluation?: string;
    prior_accumulated?: string;
    result_of_period?: string;
    other?: string;
    other_components?: string; // Mantener para compatibilidad con datos antiguos
  };
  gross_result: {
    agricultural_production: string;
    production_cost: string;
    sales: string;
    cost_of_sales: string;
    /** Calculado al guardar; enviado a origination para EBIT. */
    total?: number;
  };
  operating_result: {
    commercialization_expenses: string;
    administrative_expenses: string;
    other_income: string;
    other_expenses: string;
  };
  ordinary_result: {
    financial_expenses: string;
    exchange_difference_and_holding_result: string;
  };
  result_before_taxes: {
    extraordinary_income: string;
    extraordinary_expenses: string;
  };
  taxes: {
    income_tax: string;
  };
  purchases?: string | number | null;
  amortization_depreciation?: string | number | null;
}

export interface salesPurchasesCloudData {
  month: number;
  year: number;
  amount: number;
}

/** Período de cierre del balance (mes/año). Usado para calcular meses post-balance. */
export interface BalancePeriod {
  to_year?: number;
  to_month?: number;
}

/** Un ítem de ventas/compras locales (mes, año, monto) para post-balance. */
export interface PostBalanceLocalItem {
  month: number;
  year: number;
  amount: number;
}

/** Objeto con claves YYYYMM y valores de monto (ventas o compras por mes desde la nube). */
export type SalesPurchasesByMonth = Record<string, number | null>;

export interface ITableData {
  size: number;
  page: number;
  totalElements: number;
  totalPages: number;
}

export interface signersData {
  locality: string;
  cuit: string;
  name: string;
  email: string;
  asset_file_path: string;
  address: string
  role: string
  signer_type_code: string[]; // Multiple types support
  position?: string;
}

export interface IItem {
  cuit: string;
  id: number;
  commercial: {
    first_name: string;
    last_name: string;
  };
  company_name: string;
  producer: {
    first_name: string;
    last_name: string;
  };
  updated_date: string;
  status: {
    id: number;
    code: string;
    label?: string;
  };
  total_ars: number;
  total_usd: number;
  approved_total_ars: number;
  approved_total_usd: number;
  /** Listado comité: totales monto otorgado (calculadora), si backend los expone */
  granted_total_ars?: number | null;
  granted_total_usd?: number | null;
  usuario?: string;
  referido?: string;
  balance_sheet_code?: string | null;
  balance_processing_status?: string | null;
  balance_processing_error_code?: string | null;
  balance_processing_error_context?: string | null;
  /** Listado: gerencia (cuando el backend enriquece el listado). */
  management_id?: number | null;
  management_label?: string | null;
  /**
   * Aprobador masivo de comité (Cementos LEGACY MVP). El BFF enriquece el listado
   * solo cuando `status === 'COM'` y el flag `isCommitteeBulkApprovalEnabled` está
   * activo. En todos los demás casos llegan undefined y el front trata como `false`.
   * Source: bff-moor `application.response.ts` (campos snake_case sin mapper).
   */
  committee_can_approve?: boolean;
  /** LEGACY = aprobaciones por rol secuenciales; MEMBER_QUORUM = quórum de COMITE. */
  committee_mode?: 'LEGACY' | 'MEMBER_QUORUM';
  /** True si quedan steps pendientes tras la aprobación; false si es el último step. */
  committee_has_next?: boolean;
  /** Raw is_valid from the backend (Finished folders list, Task 53633): true/false/null. */
  is_valid?: boolean | null;
}

export type Nullable<T> = T | null;

export interface applicationFile {
  file_type: string;
  asset_file_path: string;
}
export interface contractData {
  template_name: string;
  id: string;
  external_service_id: string;
  asset_file_path: string;
  book_location: string;
  signature_type_code: string;
  status_code: string;
  isLocal?: boolean;
}

export interface manualContractData {
  template_name: string | undefined;
  asset_file_path: string;
  book_location: string;
}

export enum InstrumentationStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  RETRYING = 'RETRYING'
}

export interface InstrumentationError {
  code: string;
  message: string;
  // Nota: retryable se deriva del código usando función helper
  // timestamp se obtiene de InstrumentationProgress.completed_at cuando status = FAILED
}

export interface InstrumentationProgress {
  status: InstrumentationStatus;
  started_at?: string;
  completed_at?: string;
  error?: InstrumentationError;
  duration?: number;
}

export interface Line {
  amount: amount;
  id: number;
  instrumentation_id: string | null;
  product_line_code: string;
  instrumentation_progress?: InstrumentationProgress;
}

export interface ApplicationListData {
  id: number;
  cuit: string;
  company_name: string;
  contact_email: string;
  contact_phone: string;
  status?: {
    id: number;
    code: string;
    label: string;
  } | null;
  producer?: {
    id: number;
    first_name: string;
    last_name: string;
  } | null;
  commercial?: {
    id: number;
    first_name: string;
    last_name: string;
  } | null;
  updated_date: string;
  total_ars: number;
  total_usd: number;
  manual_total_amount: boolean;
  reason: string;
  review: string;
  extra_comments: string;
  risk_analysis: any;
  approved_total_ars: number;
  approved_total_usd: number;
  officer?: string;
  referido?: string;
  /** Raw is_valid from the backend (Finished folders list, Task 53633): true/false/null. */
  is_valid?: boolean | null;
}

/** Validity label resolved in ms-application (never recompute against the browser date — RB10). */
export type ValidityLabel = 'VALID' | 'EXPIRED' | 'NO_DATA';

/** Validity block of the detail view (snake_case mirror of the ms-application response via BFF). */
export interface ValidityData {
  is_valid: boolean | null;
  /** YYYY-MM-DD (DB DATE). */
  start_date: string | null;
  end_date: string | null;
  days_until_expiry: number | null;
  days_until_start: number | null;
  label: ValidityLabel;
  legend: string | null;
}

export interface ApprovedApplicationsData {
  cuit: string;
  company_name: string;
  referred_name?: string;
  /** Soft-rename: local-currency approved total bucket (ARS|UYU); JSON key `*_ars` unchanged. */
  total_approved_amount_ars: number;
  total_approved_amount_usd: number;
  approved_applications_count: number;
  /** Soft-rename: local-currency used bucket; JSON key `*_ars` unchanged. */
  used_amount_ars: number;
  used_amount_usd: number;
  /** Soft-rename: local-currency available bucket; JSON key `*_ars` unchanged. */
  available_amount_ars: number;
  available_amount_usd: number;
}

export interface ShareType {
  id: number;
  name: string;
}

export interface ShareCurrencyOption {
  id: number;
  code: string;
  label: string;
  description: string;
}

export interface Share {
  id: number;
  name: string;
  ticker: string;
  shareTypeId: number;
  shareTypeName: string;
  currencyId: number;
  currencyCode: string;
  currencyDescription: string;
  nominalValue: number;
  issuedQuantity: number;
  issueDate: string; // YYYY-MM-DD
  enabled: boolean;
}

export interface ShareInput {
  name: string;
  ticker: string;
  shareTypeId: number;
  currencyId: number;
  nominalValue: number;
  issuedQuantity: number;
  issueDate: string;
  enabled: boolean;
}

export interface ShareFilters {
  search?: string;
  shareTypeId?: number | null;
  enabled?: boolean | null;
}

export interface ShareFormMetadata {
  shareTypes: ShareType[];
  currencies: ShareCurrencyOption[];
}

export interface ApprovedApplicationsResponse {
  content: ApprovedApplicationsData[];
  page: number;
  size: number;
  total_elements: number;
  total_pages: number;
}
